import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@newcore/ui/buttons';
import { ButtonVariants } from '@newcore/ui/enums/variants';
import { Input } from '@newcore/ui/inputs';
import { Text } from '@newcore/ui/typography';
import { Page } from '@/components/common/page';
import { PageHeader } from '@/components/common/page-header';
import { Alert } from '@/components/ui/alert';
import { DiagnosisPending } from '@/features/mcp-tester/components/diagnosis-pending';
import { DiagnosisReport } from '@/features/mcp-tester/components/diagnosis-report';
import { McpTesterEmptyState } from '@/features/mcp-tester/components/mcp-tester-empty-state';
import { MCP_SPEC_AUTHORIZATION_URL } from '@/features/mcp-tester/constants/mcp-tester-content';
import { useDiagnoseMcpServerMutation } from '@/features/mcp-tester/hooks/use-diagnose-mcp-server-mutation';
import {
  MCP_TESTER_FORM_DEFAULTS,
  type McpTesterFormModel,
  McpTesterFormSchema,
} from '@/features/mcp-tester/mcp-tester.validation-schema';

function McpTesterPage() {
  const [checkedAddress, setCheckedAddress] = useState('');
  const diagnose = useDiagnoseMcpServerMutation();
  const form = useForm<McpTesterFormModel>({
    resolver: zodResolver(McpTesterFormSchema),
    defaultValues: MCP_TESTER_FORM_DEFAULTS,
  });
  const hasAddress = form.watch('address').trim().length > 0;
  const hasResult = Boolean(diagnose.data) || diagnose.isError;
  const isIdle = !diagnose.isPending && !hasResult;

  const runCheck = ({ address }: McpTesterFormModel) => {
    setCheckedAddress(address);
    diagnose.mutate(address);
  };

  const handleSubmit = form.handleSubmit(runCheck);

  const handlePickExample = (url: string) => {
    form.setValue('address', url);
    void handleSubmit();
  };

  const handleClear = () => {
    form.reset(MCP_TESTER_FORM_DEFAULTS);
    setCheckedAddress('');
    diagnose.reset();
  };

  return (
    <Page className='gap-8'>
      <PageHeader
        title='MCP server tester'
        description='Check whether an MCP server can be added to NewCore, and what it is missing if it cannot.'
      />
      <form className='flex flex-col gap-2' onSubmit={handleSubmit}>
        <div className='flex flex-col gap-2 sm:flex-row'>
          <Input
            {...form.register('address')}
            placeholder='https://mcp.example.com/mcp'
            aria-label='MCP server address'
            data-testid='mcp-tester-address'
          />
          <Button
            type='submit'
            disabled={!hasAddress || diagnose.isPending}
            data-testid='mcp-tester-run'
          >
            {diagnose.isPending ? 'Checking…' : 'Run check'}
          </Button>
          {(hasResult || hasAddress) && !diagnose.isPending && (
            <Button
              type='button'
              variant={ButtonVariants.SECONDARY}
              onClick={handleClear}
              data-testid='mcp-tester-clear'
            >
              Clear
            </Button>
          )}
        </div>
        <Text as='p' className='text-tertiary'>
          Nothing is created or stored. We only read what the server publishes —
          see the{' '}
          <Button variant={ButtonVariants.LINK_PRIMARY} asChild>
            <a
              href={MCP_SPEC_AUTHORIZATION_URL}
              target='_blank'
              rel='noreferrer'
            >
              MCP authorization spec
            </a>
          </Button>
          .
        </Text>
      </form>

      {isIdle && <McpTesterEmptyState onPick={handlePickExample} />}
      {diagnose.isPending && <DiagnosisPending />}
      {diagnose.isError && (
        <Alert variant='destructive'>{diagnose.error.message}</Alert>
      )}
      {diagnose.data && !diagnose.isPending && (
        <DiagnosisReport result={diagnose.data} serverUrl={checkedAddress} />
      )}
    </Page>
  );
}

export default McpTesterPage;
